import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { getThemeConfig, DEFAULT_THEME_COLOURS } from '../services/themeService'

const ThemeContext = createContext({})

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}

/**
 * Apply theme colours to the DOM as CSS custom properties
 * @param {Array<{category: string, hex_value: string}>} colours
 */
const applyThemeToDOM = (colours) => {
  const root = document.documentElement

  colours.forEach(({ category, hex_value }) => {
    // Convert category name to CSS variable format
    // e.g., 'primary_hover' -> '--color-primary-hover'
    const varName = `--color-${category.replace(/_/g, '-')}`
    root.style.setProperty(varName, hex_value)
  })
}

export const ThemeProvider = ({ children }) => {
  const [colours, setColours] = useState(DEFAULT_THEME_COLOURS)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Fetch theme config from database
  const fetchTheme = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await getThemeConfig()

      if (fetchError) {
        console.error('Error fetching theme config:', fetchError)
        setError(fetchError)
        // Apply default colours as fallback
        applyThemeToDOM(DEFAULT_THEME_COLOURS)
        return
      }

      if (data && data.length > 0) {
        setColours(data)
        applyThemeToDOM(data)
      } else {
        // No data returned, use defaults
        applyThemeToDOM(DEFAULT_THEME_COLOURS)
      }
    } catch (err) {
      console.error('Exception fetching theme config:', err)
      setError(err)
      applyThemeToDOM(DEFAULT_THEME_COLOURS)
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch theme on mount
  useEffect(() => {
    fetchTheme()
  }, [fetchTheme])

  // Refresh theme - call after saving changes
  const refreshTheme = useCallback(async () => {
    await fetchTheme()
  }, [fetchTheme])

  // Update local colours (for preview before saving)
  const updateLocalColours = useCallback((newColours) => {
    setColours(newColours)
    applyThemeToDOM(newColours)
  }, [])

  const value = {
    colours,
    loading,
    error,
    refreshTheme,
    updateLocalColours
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export default ThemeContext
